import React, { useState, useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FormattedMessage } from 'react-intl'
import PropTypes from 'prop-types'
import classnames from 'classnames'

// Utils
import history from '../../../history'
import { hasCustomerToken } from '../../../auth'

// Constants
import { OVERLAY_HELPERS } from '../../../constants/navigation'
import { PREFERENCES } from '../../../constants/messages'

// Actions
import { flashNotification } from '../../../actions/notification'
import { saveClientData } from '../../../actions/client'

// Selectors
import {
  selectClientMobile,
  selectClientSaveRequested,
  selectClientSaveSuccess,
  selectClientSaveFailure,
} from '../../../store/selectors/client'

// Components
import { If, Unless, Text, Button, Form, FormField, Page } from '../..'
import { validNumber } from '../Input/PhoneInput'

// Styles
import styles from './SetMobileForm.module.scss'

// Assets
import SmsHB from './assets/SmsHB.svg'

const SetMobileForm = ({ updating }) => {
  const dispatch = useDispatch()
  const clientMobile = useSelector(selectClientMobile)

  const isCustomer = hasCustomerToken()
  const requested = useSelector(selectClientSaveRequested)
  const success = useSelector(selectClientSaveSuccess)
  const failure = useSelector(selectClientSaveFailure)

  const [mobile, setMobile] = useState(clientMobile)

  const phone = FormField.Phone.defaultProps.name
  const phoneField = {
    name: phone,
    conditions: [validNumber()],
  }

  const onSubmit = useCallback(
    e => {
      e.preventDefault()

      if (isCustomer) {
        dispatch(
          flashNotification({
            message: PREFERENCES.duh,
            messageType: 'error',
          })
        )
      } else {
        const attributes = { mobile }

        if (!updating) {
          attributes['opted-into-sms'] = true
        }

        dispatch(saveClientData(attributes))
      }
    },
    [mobile, updating, isCustomer, dispatch]
  )

  const handleChange = useCallback(
    e => {
      setMobile(e.target.value)
    },
    [setMobile]
  )

  useEffect(() => {
    if (!updating && success) {
      history.replace(OVERLAY_HELPERS.textUpdates.getOrigin())
      dispatch(
        flashNotification({
          message: PREFERENCES.saved,
          messageType: 'success',
        })
      )
    } else if (success || failure) {
      history.goBack()
    }

    if (failure && !isCustomer) {
      dispatch(
        flashNotification({
          message: PREFERENCES.errored,
          messageType: 'error',
        })
      )
    }
  }, [success, failure, isCustomer, updating, dispatch])

  return (
    <Page.Body className={styles.body}>
      <Unless condition={updating}>
        <div className={styles.svg}>
          <SmsHB />
        </div>
      </Unless>
      <div className={classnames(styles.header, updating && styles.fillSpace)}>
        <Text size='xl' variant='neutralCool' variantCount='900'>
          <If condition={updating}>
            <FormattedMessage
              id='TextUpdates.panel.title.update'
              defaultMessage='Update your mobile number'
            />
          </If>
          <Unless condition={updating}>
            <FormattedMessage
              id='TextUpdates.panel.title.get'
              defaultMessage='Get text updates'
            />
          </Unless>
        </Text>
        <Unless condition={updating}>
          <Text variant='neutralCool' variantCount='700'>
            <FormattedMessage
              id='TextUpdates.panel.subTitle'
              defaultMessage='Easily access your most current home report. You can opt out and update your notification preferences at any time.'
            />
          </Text>
        </Unless>
      </div>
      <Form className={styles.form} fields={[phoneField]} onSubmit={onSubmit}>
        <div className={styles.inputContainer}>
          <FormField.Phone
            className={styles.inputWrapper}
            inputClassName={styles.input}
            labelText={MobileDescription}
            conditions={phoneField.conditions}
            value={mobile}
            placeholder='555-555-5555'
            onChange={handleChange}
          />
        </div>
        <div className={styles.fill} />
        <Button fullWidth primary type='submit' loading={requested}>
          <FormattedMessage id='General.buttons.save' defaultMessage='Save' />
        </Button>
      </Form>
      <Unless condition={updating}>
        <Text size='xs' variant='neutralCool' variantCount='900'>
          <FormattedMessage
            id='TextUpdates.panel.disclaimer'
            defaultMessage='By continuing, you’re giving us permission to text you information that relates to your Homebot report.'
          />
        </Text>
      </Unless>
    </Page.Body>
  )
}

SetMobileForm.propTypes = {
  updating: PropTypes.bool,
}

const MobileDescription = props => (
  <Text
    className={styles.screenReader}
    variant='neutralCool'
    variantCount='900'
  >
    <FormattedMessage
      id='TextUpdates.panel.title.edit'
      defaultMessage='Edit Mobile Number'
    />
  </Text>
)

export default SetMobileForm
